import { useEffect } from "react";

// NOTE: This key is used in Chopin file:
// app/views/journeys/showcase_component.html.haml
// It must be changed in both places if updated, and should be unique enough to avoid conflicts with other localStorage keys.
const TRANSITION_STORAGE_KEY = "userHasSeenShowcaseTransition";

const OVERLAY_ID = "showcase-reveal-root";
const SKIP_CLASS = "showcase-reveal--skip";
const HIDE_ANIMATION = "showcase-reveal-hideOverlay";
const FALLBACK_MS = 5000;

/**
 * Marks the showcase transition as seen in localStorage immediately on hydration,
 * then removes the HAML CSS overlay after its animation completes.
 */
export const useShowcaseReveal = () => {
  useEffect(() => {
    // Set "seen" immediately so a back-navigation skips the animation
    try {
      window.localStorage.setItem(TRANSITION_STORAGE_KEY, String(Date.now()));
    } catch {
      // Ignore storage errors
    }

    const overlay = document.getElementById(OVERLAY_ID);
    if (!overlay) return;

    const removeOverlay = () => overlay.remove();

    // Returning visitor — inline script already hid it
    if (overlay.classList.contains(SKIP_CLASS)) {
      removeOverlay();
      return;
    }

    // Wait for CSS animation to finish before removing
    const inner = overlay.querySelector(".showcase-reveal__overlay");

    // If we have an inner overlay element, listen for its hide animation
    if (inner) {
      // eslint-disable-next-line prefer-const
      let fallback: number | undefined;

      const handleAnimationEnd = (event: Event) => {
        if ((event as AnimationEvent).animationName === HIDE_ANIMATION) {
          if (fallback !== undefined) {
            clearTimeout(fallback);
          }
          removeOverlay();
          inner.removeEventListener("animationend", handleAnimationEnd);
        }
      };

      inner.addEventListener("animationend", handleAnimationEnd);

      // Fallback in case animationend never fires
      fallback = window.setTimeout(() => {
        removeOverlay();
        inner.removeEventListener("animationend", handleAnimationEnd);
      }, FALLBACK_MS);

      return () => {
        if (fallback !== undefined) {
          clearTimeout(fallback);
        }
        inner.removeEventListener("animationend", handleAnimationEnd);
      };
    }

    // If there's no inner element, just rely on the fallback timeout
    const fallback = window.setTimeout(removeOverlay, FALLBACK_MS);
    return () => {
      clearTimeout(fallback);
    };
  }, []);
};
